import React, { useCallback, useMemo } from 'react';
import { ListItem, ListItemText, Collapse, Box, List, ListItemButton, Stack } from '@mui/material';
import { ExpandMore, ExpandLess, Bolt } from '@mui/icons-material';
import type { CardControl } from 'c-types';
import type { Campaign } from 'c-sdk';
import { CampaignStatus, PermissionName, UserType } from 'c-sdk';
import { useUserPermissions } from 'c-auth-module/Hooks';
import { IfHasAllPermissions } from 'c-auth-module/Components';
import { useAuthenticatedUser } from 'c-hooks';
import type { UseBoolean } from 'react-hanger';
import { useBoolean } from 'react-hanger';
import { useHistory, generatePath } from 'react-router-dom';
import { useCommonTranslation } from 'c-translation';
import { PostAuthRoutes } from 'c-routes';
import { usesInsertionOrderTargeting } from 'c-main/Lib';
import { Alert, Button, DialogV2 } from 'c-components';

import {
    NewCampaignPermissions,
    CampaignUpdateDialogPermissions,
    BookingSummaryPermissions,
} from './constants';

interface SidebarControlsListProps {
    campaign: Campaign;
    campaignShareDialogState: { setTrue: () => void };
    campaignBookingSummaryState: { setTrue: () => void };
    pcaState: { setTrue: () => void };
    pptPcaState: { setTrue: () => void };
    iOIdsState: { setTrue: () => void };
    happyDemicsState: { setTrue: () => void };
    reportingInputsState: { setTrue: () => void };
    summaryCustomizationState: { setTrue: () => void };
    pcaCustomizationState: { setTrue: () => void };
    creativeScheduleCheckState: { setTrue: () => void };
    /** Archives or unarchives (whichever the campaign isn't); resolves true when it worked. */
    performArchive: () => Promise<boolean>;
    IsArchiving: boolean;
    HasFailedArchiving: boolean;
    ArchivingError: any;
    groupActions: UseBoolean;
}

const ShareCampaignUserTypes = [UserType.Buyer, UserType.Admin];

const SidebarControlsList: React.FC<SidebarControlsListProps> = ({
    campaign,
    campaignShareDialogState,
    campaignBookingSummaryState,
    pcaState,
    pptPcaState,
    iOIdsState,
    happyDemicsState,
    performArchive,
    IsArchiving,
    HasFailedArchiving,
    reportingInputsState,
    summaryCustomizationState,
    pcaCustomizationState,
    creativeScheduleCheckState,
    ArchivingError,
    groupActions,
}) => {
    const { hasAll } = useUserPermissions();
    const { chosenUserType } = useAuthenticatedUser();
    const history = useHistory();
    const t = useCommonTranslation();

    const id = campaign.id;
    const cloneLink = useMemo(
        () => generatePath(PostAuthRoutes.Campaigns.CloneCampaign, { id }),
        [id],
    );
    const isCampaignArchived = useMemo(
        () => campaign?.status === CampaignStatus.CampaignArchived,
        [campaign?.status],
    );

    // Archiving asks first; unarchiving is the undo, so it stays one click.
    const archiveConfirm = useBoolean(false);
    const openArchiveConfirm = archiveConfirm.setTrue;
    const closeArchiveConfirm = archiveConfirm.setFalse;
    const confirmArchive = useCallback(async () => {
        if (await performArchive()) {
            closeArchiveConfirm();
        }
    }, [performArchive, closeArchiveConfirm]);

    const allControls = useMemo<CardControl[]>(
        () => {
            const base: CardControl[] = [
                {
                    label: t('Modules.Main.Campaigns.EditLineItems.cloneCampaign'),
                    path: cloneLink,
                    permissions: NewCampaignPermissions,
                    group: 'actions',
                },
                {
                    label: (
                        <>
                            {isCampaignArchived
                                ? t('Modules.Main.Campaigns.Overview.ActionLabels.unarchive')
                                : t('Modules.Main.Campaigns.Overview.ActionLabels.archive')}
                        </>
                    ),
                    preventCloseOnClick: true,
                    permissions: CampaignUpdateDialogPermissions,
                    onClick: isCampaignArchived ? performArchive : openArchiveConfirm,
                    disabled: IsArchiving,
                    isLoading: IsArchiving,
                    error: HasFailedArchiving ? ArchivingError : undefined,
                    group: 'actions',
                },

                // Actions — everything else
                {
                    label: t('Modules.Main.Campaigns.Overview.ActionLabels.share'),
                    permissions: [PermissionName.CampaignCan_invite_user],
                    onClick: campaignShareDialogState.setTrue,
                    userTypes: ShareCampaignUserTypes,
                    group: 'actions',
                },
                {
                    label: t('Modules.Main.Campaigns.Overview.ActionLabels.bookingSummary'),
                    permissions: BookingSummaryPermissions,
                    onClick: campaignBookingSummaryState.setTrue,
                    group: 'actions',
                },
                {
                    label: t('Modules.Main.Campaigns.Overview.ActionLabels.cad'),
                    permissions: [PermissionName.AdminCampaignCad],
                    onClick: pcaState.setTrue,
                    group: 'actions',
                },
                {
                    label: t('Modules.Main.Campaigns.Overview.ActionLabels.pca'),
                    permissions: [PermissionName.PowerpointPcareportRead],
                    onClick: pptPcaState.setTrue,
                    group: 'actions',
                },
                {
                    label: t('Modules.Main.Campaigns.Overview.ActionLabels.reportingInputs'),
                    onClick: reportingInputsState.setTrue,
                    permissions: [PermissionName.AdminCampaignReporting_inputs],
                    group: 'actions',
                },
                {
                    label: t('Modules.Main.Campaigns.Overview.ActionLabels.customizeSummary'),
                    onClick: summaryCustomizationState.setTrue,
                    permissions: [PermissionName.AdminCampaignCustomize_summary],
                    group: 'actions',
                },
                {
                    label: t('Modules.Main.Campaigns.Overview.ActionLabels.customizePca'),
                    onClick: pcaCustomizationState.setTrue,
                    permissions: [PermissionName.AdminCampaignCustomize_pca],
                    group: 'actions',
                },
                {
                    label: 'Creative Schedule Check',
                    onClick: creativeScheduleCheckState.setTrue,
                    permissions: [PermissionName.AdminCampaignCreative_schedule_check],
                    group: 'actions',
                },
            ] as (CardControl & { group: 'manage' | 'actions' })[];

            // Hand-typed IO ids are for the DSP types without targeting. A vistar_us2 campaign takes
            // its insertion orders from the schedule page's rules and ignores this list.
            if (
                campaign.type !== 'default' &&
                campaign.type !== 'linear' &&
                !usesInsertionOrderTargeting(campaign)
            ) {
                base.push({
                    label: t('Modules.Main.Campaigns.Overview.ActionLabels.ioIds'),
                    permissions: [PermissionName.AdminCampaignIo_ids],
                    onClick: iOIdsState.setTrue,
                    group: 'actions',
                });
            }
            if (campaign?.buyer?.happydemics_client_key_name != null) {
                base.push({
                    label: t('Modules.Main.Campaigns.Overview.ActionLabels.happyDemics'),
                    permissions: [PermissionName.UniledPortalHappyDemicsRead],
                    onClick: happyDemicsState.setTrue,
                    group: 'actions',
                });
            }
            return base;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            t,
            cloneLink,
            isCampaignArchived,
            performArchive,
            openArchiveConfirm,
            IsArchiving,
            HasFailedArchiving,
            ArchivingError,
            campaignShareDialogState.setTrue,
            ShareCampaignUserTypes,
            campaignBookingSummaryState.setTrue,
            pcaState.setTrue,
            pptPcaState.setTrue,
            reportingInputsState.setTrue,
            summaryCustomizationState.setTrue,
            pcaCustomizationState.setTrue,
            creativeScheduleCheckState.setTrue,
            campaign.type,
            campaign?.buyer?.happydemics_client_key_name,
            iOIdsState.setTrue,
            happyDemicsState.setTrue,
        ],
    );

    const filtered = useMemo(
        () =>
            allControls
                .filter(c => hasAll(c.permissions ?? []))
                .filter(c => (c.userTypes?.length ? c.userTypes.includes(chosenUserType) : true)),
        [allControls, hasAll, chosenUserType],
    );

    const actionControls = useMemo(
        () => filtered.filter((c: any) => c.group === 'actions'),
        [filtered],
    );

    if (filtered.length === 0) return null;

    const renderGroup = (
        title: string,
        icon: React.ReactNode,
        openState: ReturnType<typeof useBoolean>,
        controls: CardControl[],
    ) => {
        if (controls.length === 0) return null;

        return (
            <>
                <ListItem disablePadding>
                    <ListItemButton onClick={openState.toggle}>
                        <Stack direction="row" alignItems="center" gap={1}>
                            {icon}
                            <ListItemText primary={title} />
                            {openState.value ? <ExpandLess /> : <ExpandMore />}
                        </Stack>
                    </ListItemButton>
                </ListItem>

                {/* The parent is a <List> (Sidebar.tsx): a <ul> may only hold <li>s, so the collapsing
                    sub-list sits in an <li> of its own, as a <ul> of its own. */}
                <Box component="li">
                    <Collapse in={openState.value} timeout="auto" unmountOnExit>
                        <List disablePadding sx={{ pl: 2 }}>
                            {controls.map((control, index) => (
                                <ListItem key={(control as any).path ?? index} disablePadding>
                                    <ListItemButton
                                        onClick={() => {
                                            if ((control as any).path) {
                                                history.push((control as any).path as string);
                                            } else if (control.onClick) {
                                                control.onClick();
                                            }
                                        }}
                                        disabled={control.disabled}
                                    >
                                        <ListItemText
                                            primary={
                                                <IfHasAllPermissions
                                                    permissions={control.permissions}
                                                >
                                                    {control.label}
                                                </IfHasAllPermissions>
                                            }
                                        />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    </Collapse>
                </Box>
            </>
        );
    };

    return (
        <>
            <DialogV2
                open={archiveConfirm.value}
                onClose={closeArchiveConfirm}
                title="Modules.Main.Campaigns.Overview.ArchiveDialog.title"
                description="Modules.Main.Campaigns.Overview.ArchiveDialog.description"
                actions={
                    <Stack direction="row" gap={2}>
                        <Button variant="outlined" onClick={closeArchiveConfirm}>
                            {t('Dialog.defaultCancelLabel')}
                        </Button>
                        <Button onClick={confirmArchive} disabled={IsArchiving}>
                            {t('Modules.Main.Campaigns.Overview.ActionLabels.archive')}
                        </Button>
                    </Stack>
                }
            >
                {HasFailedArchiving && ArchivingError && (
                    <Alert severity="error">{String(ArchivingError)}</Alert>
                )}
            </DialogV2>
            {renderGroup(
                t('Modules.Main.Campaigns.Overview.OverviewSection.actions'),
                <Bolt color="primary" />,
                groupActions,
                actionControls,
            )}
        </>
    );
};

export default SidebarControlsList;
